/**
 * The one HTTP path out of the web app.
 *
 * It attaches the bearer token, and on a 401 it refreshes ONCE through the
 * single-flight queue in ./refresh and replays the request. Callers therefore
 * never see a token expiry — they see either data or a real error.
 */

import type { ApiError } from '@cyphex/types';
import { API_BASE_URL } from './env';
import { ApiRequestError } from './errors';
import { refreshAccessToken } from './refresh';
import { getAccessToken } from './token';

export interface RequestOptions {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Replays the first response for 24h — required for anything that sends. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Auth endpoints opt out: refreshing a failed login would be a loop. */
  skipRefresh?: boolean;
}

/** Paths whose own 401 IS the answer — never retry them behind the caller's back. */
const NO_REFRESH = /^\/auth\/(login|register|refresh|logout|mfa)/;

function envelopeOf(payload: unknown): ApiError['error'] | null {
  if (typeof payload !== 'object' || payload === null || !('error' in payload)) return null;
  const error = (payload as { error: unknown }).error;
  if (typeof error !== 'object' || error === null || !('code' in error)) return null;
  return error as ApiError['error'];
}

async function send(path: string, options: RequestOptions, token: string | null): Promise<Response> {
  const { method = 'GET', body, idempotencyKey, signal } = options;
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json; charset=utf-8';
  if (token !== null) headers.Authorization = `Bearer ${token}`;
  if (idempotencyKey !== undefined) headers['Idempotency-Key'] = idempotencyKey;

  return fetch(`${API_BASE_URL}${path}`, {
    method,
    headers,
    signal,
    // Same-origin so the httpOnly refresh cookie rides along on /auth calls.
    credentials: 'same-origin',
    body: body === undefined ? undefined : JSON.stringify(body),
  });
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const canRefresh = options.skipRefresh !== true && !NO_REFRESH.test(path);

  // A RELOAD STARTS WITH NO TOKEN IN MEMORY — it survives only in the httpOnly
  // refresh cookie. Sending anyway meant every query that mounted before the
  // boot rotation finished took a GUARANTEED 401 and then retried. It worked,
  // but it put three red failures in the console on every page load, which
  // trains you to ignore the console.
  //
  // Rotating first costs nothing: the rotation is single-flight, so concurrent
  // callers await one shared promise and then all send with a valid token. For
  // a genuinely signed-out visitor it resolves to null almost immediately and
  // the request proceeds unauthenticated, so the caller still gets the 401 that
  // is the honest answer.
  let token = getAccessToken();
  if (token === null && canRefresh) token = await refreshAccessToken();

  let response: Response;
  try {
    response = await send(path, options, token);
    if (response.status === 401 && canRefresh) {
      // Still one retry: a token can expire between that rotation and this
      // send, and concurrent 401s all wait on the same rotation. See ./refresh.
      const refreshed = await refreshAccessToken();
      if (refreshed !== null) response = await send(path, options, refreshed);
    }
  } catch (cause) {
    if (options.signal?.aborted === true) throw cause;
    throw new ApiRequestError(0, {
      code: 'dependency_unavailable',
      message: 'Network unreachable.',
      requestId: 'local',
    });
  }

  if (response.status === 204) return undefined as T;
  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const envelope = envelopeOf(payload);
    throw new ApiRequestError(response.status, {
      code: envelope?.code ?? 'internal_error',
      message: envelope?.message ?? response.statusText,
      requestId: envelope?.requestId ?? response.headers.get('x-request-id') ?? 'unknown',
      ...(envelope?.retryAfter === undefined ? {} : { retryAfter: envelope.retryAfter }),
      ...(envelope?.fields === undefined ? {} : { fields: envelope.fields }),
      ...(envelope?.details === undefined ? {} : { details: envelope.details }),
    });
  }
  return payload as T;
}

export const apiGet = <T>(path: string, signal?: AbortSignal): Promise<T> =>
  apiRequest<T>(path, { method: 'GET', signal });

export const apiPost = <T>(path: string, body?: unknown, options: RequestOptions = {}): Promise<T> =>
  apiRequest<T>(path, { ...options, method: 'POST', body });

export const apiPatch = <T>(path: string, body: unknown, signal?: AbortSignal): Promise<T> =>
  apiRequest<T>(path, { method: 'PATCH', body, signal });

export const apiPut = <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> =>
  apiRequest<T>(path, { method: 'PUT', body, signal });

/**
 * `body` is optional and rarely used — but a moderator deletion carries a
 * reason, and the alternative (a reason in the query string) would leak it into
 * every access log.
 */
export const apiDelete = <T>(path: string, body?: unknown, signal?: AbortSignal): Promise<T> =>
  apiRequest<T>(path, { method: 'DELETE', ...(body === undefined ? {} : { body }), signal });

export { ApiRequestError } from './errors';
